import type { MutableRefObject } from 'react';
import { facebookSDKReady } from 'src/lib/facebook/facebook';

/**
 * How long a launch waits for the Facebook SDK before it gives up. The SDK
 * script can hang (a slow connection, a content blocker), and without a limit
 * the connect button would stay in its loading state with no way out.
 */
export const FACEBOOK_SDK_READY_TIMEOUT_MS = 15_000;

/** Holds the timer of the wait in progress, so its owner can clear it. */
export type tFacebookSDKTimeoutRef = MutableRefObject<ReturnType<
  typeof setTimeout
> | null>;

/**
 * Clears the wait timer held in `timeoutRef`, if any. Owners call it when they
 * unmount, so no timer outlives them.
 *
 * @param {tFacebookSDKTimeoutRef} timeoutRef - The owner's timer ref.
 * @returns {void}
 */
export function clearFacebookSDKTimeout(
  timeoutRef: tFacebookSDKTimeoutRef,
): void {
  if (timeoutRef.current === null) return;
  clearTimeout(timeoutRef.current);
  timeoutRef.current = null;
}

/**
 * Waits for the Facebook SDK for at most `FACEBOOK_SDK_READY_TIMEOUT_MS`.
 *
 * The timer lives in `timeoutRef` and is cleared when the SDK becomes ready, so
 * none is left running. An SDK that arrives after the limit only clears a
 * timer that is still its own, never the timer of a newer wait.
 *
 * @param {tFacebookSDKTimeoutRef} timeoutRef - Where the wait keeps its timer.
 * @returns {Promise<boolean>} True once the SDK is ready, false at the limit.
 */
export function waitForFacebookSDK(
  timeoutRef: tFacebookSDKTimeoutRef,
): Promise<boolean> {
  clearFacebookSDKTimeout(timeoutRef);
  return new Promise<boolean>((resolve) => {
    const timeout = setTimeout(() => {
      if (timeoutRef.current === timeout) timeoutRef.current = null;
      resolve(false);
    }, FACEBOOK_SDK_READY_TIMEOUT_MS);
    timeoutRef.current = timeout;

    void facebookSDKReady.then(() => {
      if (timeoutRef.current === timeout) clearFacebookSDKTimeout(timeoutRef);
      resolve(true);
    });
  });
}
